import { Box, Checkbox, Stack, TableCell, TableHead, TableRow, Tooltip } from '@mui/material';
import React from 'react';

import { menuTypes, tableTypes, UIMenu } from '@/shared/components';
import { globalEnums } from '@/shared/duck';

interface TableHeaderProps<DataItem> extends Partial<tableTypes.MuiTableContentProps<DataItem>> {
  columns: tableTypes.ColumnProps<DataItem>[];
  withEmptyItemActions: boolean;
  headerItemActions?: menuTypes.UIMenuProps;
  onSort?: (columnKey: string) => void;
  sortConfig?: {
    sortBy: string;
    sortOrder: 'asc' | 'desc';
  };
}

const TableHeader = <DataItem, >({
  columns,
  onSort,
  sortConfig,
  withEmptyItemActions,
  itemSelection,
  headerItemActions,
}: TableHeaderProps<DataItem>) => {
  const selectable = Boolean(itemSelection);

  const isComplexMenu = React.useMemo(() => {
    if (headerItemActions) {
      return headerItemActions.footerItems?.length ||
             headerItemActions.headerText?.length ||
             headerItemActions.headerSubText?.length ||
             headerItemActions.sx;
    } else {
      return false;
    }
  }, [headerItemActions]);

  const renderSortIcon = (columnKey: string) => {
    if (!sortConfig || columnKey !== sortConfig.sortBy) {
      return null;
    }

    return (
      <Box component='span' sx={{ ml: 1, display: 'inline-flex', alignItems: 'center' }}>
        {sortConfig.sortOrder === 'asc' ? '↑' : '↓'}
      </Box>
    );
  };

  const handleHeaderClick = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  return (
    <TableHead>
      <TableRow>
        {selectable && (
          <TableCell padding='checkbox'>
            <Checkbox
              color='primary'
              indeterminate={itemSelection?.itemSelectionApi?.isPartiallySelected}
              checked={itemSelection?.itemSelectionApi?.isAllSelected ||
                       itemSelection?.itemSelectionApi?.isPartiallySelected}
              onChange={itemSelection?.itemSelectionApi?.onToggleAll}
              inputProps={{
                'aria-label': 'select_all',
              }}
              disabled={itemSelection?.disabled}
            />
          </TableCell>
        )}

        {columns?.map((col) => {
          const headContent = col.customHeadRenderer ? col.customHeadRenderer(col) : col.title;

          return (
            <TableCell
              key={col.columnKey.toString()}
              onClick={() => col.sortable && handleHeaderClick(col.columnKey.toString())}
              sx={{
                verticalAlign: col.headerInfo?.verticalAlign,
                textWrap: 'nowrap',
                width: col.width,
                fontWeight: '600',
                cursor: col.sortable ? 'pointer' : 'default',
              }}
            >
              <Tooltip
                arrow
                key={'header-cell-tooltip' + col.columnKey.toString()}
                title={col.headerInfo?.cellTooltipText || ''}
                placement={col.headerInfo?.tooltipPlace || 'top'}
              >
                <Stack
                  gap={0.25}
                  flexDirection={'row'}
                >
                  {headContent}

                  {col.headerInfo?.icon && (
                    <Tooltip
                      arrow
                      key={'header-icon-tooltip' + col.columnKey.toString()}
                      title={col.headerInfo?.iconTooltipText || ''}
                      placement={col.headerInfo?.tooltipPlace || 'bottom'}
                    >
                      <Box display='flex' alignItems='center'>
                        {col.headerInfo.icon}
                      </Box>
                    </Tooltip>
                  )}

                  {col.sortable && renderSortIcon(col.columnKey.toString())}
                </Stack>
              </Tooltip>
            </TableCell>
          );
        })}

        {headerItemActions && (
          <TableCell
            onClick={(e: any) => e.stopPropagation()}
            sx={{ textAlign: 'right' }}
          >
            <UIMenu
              type={isComplexMenu
                ? globalEnums.EMenuType.COMPLEX
                : globalEnums.EMenuType.BASIC}
              itemList={headerItemActions.itemList}
              headerText={headerItemActions.headerText}
              headerSubText={headerItemActions.headerSubText}
              footerItems={headerItemActions.footerItems}
              menuIconC={headerItemActions.menuIconC}
              sx={headerItemActions.sx}
            />
          </TableCell>
        )}

        {/* Note: add an empty title for extra column with menu btn */}
        {withEmptyItemActions && (
          <TableCell padding='checkbox' />
        )}
      </TableRow>
    </TableHead>
  );
};

export default TableHeader;